import { useEffect, useState } from 'react';
import { Button, Typography } from '@mui/material';
import { isNil } from 'ramda';

import TalentsRepository, { FetchTalentListParams } from 'src/repositories/manager/TalentsRepository';
import { TalentSearch } from 'src/types/resources/TalentSearch';
import useModals from 'src/hooks/useModals';
import useLoading from 'src/hooks/useLoading';
import { useFeatureFlags } from 'src/hooks';
import SearchForm from 'src/components/SearchForm';
import Loader from 'src/components/Loader';
import Box from 'src/components/Box';

import TalentsList, { TalentsListProps } from './TalentsList';
import TalentsTable from './TalentsTable';
import styles from './styles';

type SelectTalentProps = {
  onSelectTalent: (talent: TalentSearch) => void;
  initialTalentId?: number;
};

const SelectTalent: React.FC<SelectTalentProps> = props => {
  const { onSelectTalent, initialTalentId } = props;
  const { hideModal } = useModals();
  const { featureEnableReversedRateCalculation } = useFeatureFlags();
  const [searchValue, setSearchValue] = useState<string>('');
  const [selectedTalent, setSelectedTalent] = useState<TalentSearch | null>(null);
  const [fetchedTalents, setFetchedTalents] = useState<TalentSearch[]>([]);
  const { funcWithLoading: fetchTalents, isFinished: isTalentsFetchingFinished } = useLoading(TalentsRepository.index);

  const handleListItemClick: TalentsListProps['onTalentClick'] = (event, talent) => {
    setSelectedTalent(talent);
  };

  const handleSelectTalent = () => {
    onSelectTalent(selectedTalent);
    hideModal();
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  useEffect(() => {
    (async () => {
      const params: FetchTalentListParams = {
        ordering: 'first_name,last_name',
        isTalentApprovedByRevuud: true,
      };
      if (searchValue) {
        params.search = searchValue;
      }
      const response = await fetchTalents(params);
      setFetchedTalents(response.results);
      if (initialTalentId && !selectedTalent) {
        const preSelected = response.results.find(t => t.id === initialTalentId);
        if (preSelected) setSelectedTalent(preSelected);
      }
    })();
  }, [searchValue]);

  return (
    <Box sx={featureEnableReversedRateCalculation ? styles.tableWrapper : styles.wrapper}>
      <Box sx={styles.header}>
        <Typography variant="h3" sx={styles.title}>
          Select Talent
        </Typography>
        <SearchForm placeholder="Search by name" onSearch={handleSearch} searchEntityType="engagementTalent" />
      </Box>
      <Box sx={styles.body}>
        {featureEnableReversedRateCalculation && (
          <TalentsTable
            talents={fetchedTalents}
            selectedTalentId={selectedTalent?.id}
            onTalentSelect={setSelectedTalent}
            loading={!isTalentsFetchingFinished}
          />
        )}
        {!featureEnableReversedRateCalculation && !isTalentsFetchingFinished && <Loader />}
        {!featureEnableReversedRateCalculation && isTalentsFetchingFinished && (
          <TalentsList
            talents={fetchedTalents}
            selectedTalentId={selectedTalent?.id}
            onTalentClick={handleListItemClick}
          />
        )}
      </Box>
      <Box sx={styles.footer}>
        <Button variant="contained" sx={styles.button} disabled={isNil(selectedTalent)} onClick={handleSelectTalent}>
          Add
        </Button>
        <Button variant="outlined" onClick={hideModal} sx={styles.button}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default SelectTalent;
